import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/useAuth";
import { useBranding } from "@/hooks/useBranding";
import { userHasPermission } from "@/lib/permissions";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useIsMobile } from "@/hooks/use-mobile";
import type { Permission } from "@shared/contracts/permissions";
import {
  LayoutDashboard,
  Ticket,
  CreditCard,
  FolderKanban,
  ListTodo,
  PlusCircle,
  Menu,
  LogOut,
  Settings,
  FolderOpen,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import logoDefault from "@/assets/logo-default.png";

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  permission?: Permission;
}

const navItems: NavItem[] = [
  { name: "Dashboard", href: "/", icon: LayoutDashboard, permission: "portal.dashboard.read" },
  { name: "Tickets", href: "/tickets", icon: Ticket, permission: "portal.tickets.read" },
  { name: "Projects", href: "/projects", icon: FolderKanban, permission: "portal.projects.read" },
  { name: "Services", href: "/billing", icon: CreditCard, permission: "portal.services.read" },
  { name: "Tasks", href: "/tasks", icon: ListTodo, permission: "portal.tasks.read" },
  { name: "Invoices", href: "/invoices", icon: CreditCard, permission: "portal.invoices.read" },
  { name: "Files", href: "/files", icon: FolderOpen, permission: "portal.files.read" },
  { name: "Suppliers", href: "/partners", icon: Users, permission: "portal.partners.manage" },
  { name: "New Request", href: "~/request", icon: PlusCircle, permission: "portal.requests.create" },
];

export function ClientNavbar() {
  const [location] = useLocation();
  const { user } = useAuth();
  const { logoUrl, brandName } = useBranding();
  const isMobile = useIsMobile();

  const initials =
    (user?.firstName?.[0] ?? "") + (user?.lastName?.[0] ?? user?.email?.[0] ?? "");

  const visibleNavItems = navItems.filter(
    (item) =>
      !item.permission ||
      userHasPermission(user?.effectivePermissions, item.permission),
  );

  const handleLogout = async () => {
    try {
      await apiRequest("POST", "/api/auth/logout");
    } catch {
      // ignore
    }
    await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    window.location.href = "/login";
  };

  const NavLinks = () => (
    <>
      {visibleNavItems.map((item) => {
        const pathForActive = item.href.startsWith("~/")
          ? item.href.slice(1)
          : item.href;
        const isActive =
          pathForActive === "/"
            ? location === "/" || location === ""
            : location === pathForActive ||
              location.startsWith(`${pathForActive}/`);

        return (
          <Link key={item.href} href={item.href}>
            <span
              className={cn(
                "flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer",
                isActive
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
              )}
            >
              <item.icon className="h-4 w-4" />
              {item.name}
            </span>
          </Link>
        );
      })}
    </>
  );

  return (
    <header className="sticky top-0 z-40 border-b bg-background">
      <div className="flex h-14 items-center gap-4 px-4 sm:px-6">
        {/* Logo */}
        <Link href="~/portal">
          <img
            src={logoUrl || logoDefault}
            alt={brandName}
            className="h-8 object-contain cursor-pointer"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1 flex-1">
          <NavLinks />
        </nav>

        {/* Mobile menu */}
        {isMobile && (
          <div className="flex-1 md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="top" className="pt-10">
                <nav className="flex flex-col gap-1">
                  <NavLinks />
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        )}

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2">
              <Avatar className="h-7 w-7">
                <AvatarFallback className="bg-primary/10 text-primary text-xs">
                  {initials.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="hidden sm:inline text-sm">
                {user?.firstName ?? user?.email}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            {userHasPermission(user?.effectivePermissions, "portal.account.read") && (
              <Link href="~/portal/account">
                <DropdownMenuItem className="cursor-pointer">
                  <Settings className="mr-2 h-4 w-4" />
                  Account Settings
                </DropdownMenuItem>
              </Link>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
